import { PublicService } from '@features/public-reservation/types';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useFormatCurrency } from '@features/currency/currency.hooks';
import type { SupportedCurrency } from '@features/currency/currency.types';

const PRIMARY = '#D02964';

export const PublicServiceItem = ({
  isSelected,
  durationMin,
  displayName,
  hasDescription,
  service,
  onSelect,
  preferredCurrency,
}: {
  isSelected: boolean;
  durationMin: number;
  displayName: string;
  hasDescription: boolean;
  service: PublicService;
  onSelect?: (s: PublicService) => void;
  preferredCurrency: SupportedCurrency;
}) => {
  const { t } = useIntlTranslations('publicServiceItem');
  const { format: formatCurrency } = useFormatCurrency(preferredCurrency);
  return (
    <Box
      key={service.id}
      sx={{
        borderRadius: '14px',
        border: '2px solid',
        borderColor: isSelected ? PRIMARY : 'divider',
        bgcolor: isSelected ? `${PRIMARY}10` : '#fff',
        overflow: 'hidden',
        transition: 'all 0.15s',
        boxShadow: isSelected ? `0 2px 12px ${PRIMARY}25` : '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* Hlavní řádek */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        sx={{
          p: 1,
          cursor: onSelect ? 'pointer' : 'default',
          '&:hover': onSelect ? { bgcolor: `${PRIMARY}08` } : {},
          '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: -2 },
        }}
        onClick={() => onSelect?.(service)}
        onKeyDown={event => {
          if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onSelect(service);
          }
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }} direction="row" alignItems="center" flexGrow={1}>
          <Typography
            variant="body1"
            fontWeight={isSelected ? 700 : 500}
            color="text.primary"
            noWrap
          >
            {displayName}
          </Typography>
          {durationMin > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
              <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {durationMin} min
              </Typography>
            </Stack>
          )}
        </Stack>
        <Chip
          label={formatCurrency(service.basePrice, undefined, 0, 0)}
          size="small"
          sx={{
            flexShrink: 0,
            bgcolor: isSelected ? PRIMARY : `${PRIMARY}15`,
            color: isSelected ? '#fff' : PRIMARY,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </Stack>

      {/* Accordion s popisem */}
      {hasDescription && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            '&::before': { display: 'none' },
            borderTop: '1px solid',
            borderColor: isSelected ? `${PRIMARY}30` : 'divider',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            sx={{
              minHeight: 32,
              px: 2,
              py: 0,
              '& .MuiAccordionSummary-content': { my: 0.5 },
            }}
            onClick={e => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary">
              {t('moreAboutService')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {service.publicDescription}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
